<template>
  <div>
    <div class="d-flex justify-end">
      <v-btn v-if="canPrint" outlined @click="print()">{{
        $vuetify.lang.t("$vuetify.printSetting.print")
      }}</v-btn>
      <v-btn v-if="canPrint" outlined class="ml-2" @click="printLabel()">{{
        $vuetify.lang.t("$vuetify.printSetting.printLabel")
      }}</v-btn>
    </div>
    <!-- Alert for inventory shortages (NEEDS_REVIEW entries) -->
    <v-alert v-if="hasInventoryShortages" type="warning" prominent class="mb-4">
      <div>
        <div class="font-weight-bold">{{ $vuetify.lang.t('$vuetify.worksheet.inventoryShortageAlert.title') }}</div>
        <div class="text-body-2">{{ $vuetify.lang.t('$vuetify.worksheet.inventoryShortageAlert.message', shortageCount) }}</div>
      </div>
    </v-alert>

    <div v-if="coa && coa.length > 0" class="mb-4">
      <v-alert v-if="missingCoaInventory && missingCoaInventory.length > 0" type="warning" > Inventory with missing CoA {{missingCoaInventory.join(', ')}} </v-alert>

      <v-card v-if="labResults">
        <v-card-title style="cursor: pointer" @click="expandCard = !expandCard">
          {{ $vuetify.lang.t("$vuetify.worksheet.foundCoa", coa.length) }}
        </v-card-title>

        <v-expand-transition>

          <v-list v-if="expandCard">
            <download class="mx-2" v-model="invertedLabResults" fileName="CoA Estimate"/>

            <v-list-item-group>
              <v-list-item v-for="element in labResults" :key="element.id">
                <v-list-item-content>
                  <v-list-item-title>{{
                    element.name
                  }}</v-list-item-title>

                  <v-list-item-subtitle>{{
                    element.symbol
                  }}</v-list-item-subtitle>
                  
                </v-list-item-content>
                <v-list-item-action>
                  <div>{{ element.percentage ? element.percentage.toFixed(6):0}}</div>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-expand-transition>
      </v-card>
    </div>

    <div class="text-caption my-2" v-if="worksheet.deductibleWeight">
      {{ $vuetify.lang.t('$vuetify.base.deductibleWeightPerEntry') }}:
      {{ worksheet.deductibleWeight | formatNumber }}
      {{ productInfo ? productInfo.measureWeight : "" }}
    </div>

    <v-data-table :items="groupedEntries" :headers="headers">
      <template v-slot:item.index="{ index }"> {{ index + 1 }}. </template>
      <!--
        Show when the material was WEIGHED (createTime), not when the row was last
        touched (updateTime). updateTime carries ON UPDATE CURRENT_TIMESTAMP in
        MySQL, so any later edit moved this column to "now" -- which is what
        xinyan Lin reported on 2026-08-01: "When reassign, Time change to current
        time. We still want to remain the acutal time stamp."
      -->
      <template v-slot:item.createTime="{ item }">
        {{ item.createTime | formatDateTime }}
      </template>
      <template v-slot:item.unit="{ item }">
        <div v-if="item.unit">
          {{ item.unit }} {{ productInfo ? productInfo.measureUnit : "" }}
          <div class="text-caption">
            {{ item.amountPerUnit | formatNumber }}
            {{ productInfo ? productInfo.measureWeight : "" }}/{{
              productInfo ? productInfo.measureUnit : ""
            }}
          </div>
        </div>
      </template>
      <template v-slot:item.grossWeight="{ item }">
        <div class="d-flex align-center">
          <v-tooltip 
            v-if="item.inventoryDecisionStatus === 'NEEDS_REVIEW'"
            bottom
            max-width="400"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                x-small
                color="warning"
                class="mr-2"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon x-small left>mdi-alert</v-icon>
                {{ $vuetify.lang.t('$vuetify.worksheet.status.needsReview') }}
              </v-chip>
            </template>
            <span>
              <strong>{{ $vuetify.lang.t('$vuetify.worksheet.inventoryShortfall') }}</strong>
              <br/>
              {{ formatShortageMessage(item) }}
            </span>
          </v-tooltip>
          <v-chip
            v-else-if="item.inventoryDecisionStatus === 'BACKORDER'"
            x-small
            color="info"
            class="mr-2"
            title="Backorder - waiting for inventory"
          >
            <v-icon x-small left>mdi-clock-outline</v-icon>
            {{ $vuetify.lang.t('$vuetify.worksheet.status.backorder') }}
          </v-chip>
        </div>
        {{ item.grossWeight | formatNumber }}
        {{ productInfo ? productInfo.measureWeight : "" }}
        <div
          class="text-caption"
          v-if="getDeductibleWeight(item) > 0"
        >
          {{ $vuetify.lang.t("$vuetify.base.deductibleWeight") }}:
          {{ getDeductibleWeight(item) | formatNumber }}
          {{ productInfo ? productInfo.measureWeight : "" }}
        </div>
        <div class="text-caption" v-if="item._isGrouped && item._groupEntries && item._groupEntries.length > 0">
          <div 
            class="font-weight-medium d-flex align-center" 
            style="cursor: pointer; user-select: none;"
            @click="toggleGroupExpansion(item.entryGroupId)"
          >
            <v-icon small class="mr-1">
              {{ expandedGroups[item.entryGroupId] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
            </v-icon>
            <span>
              {{ $vuetify.lang.t('$vuetify.worksheet.inventoriesUsed', item._groupEntries.length) }}
              <span class="ml-1 grey--text text--darken-1">
                ({{ getTotalReservedWeight(item._groupEntries) | formatNumber }} {{ productInfo ? productInfo.measureWeight : "" }} {{ worksheet.status === 'USED' ? $vuetify.lang.t('$vuetify.worksheet.consumed') : $vuetify.lang.t('$vuetify.worksheet.reserved') }})
              </span>
            </span>
          </div>
          <v-expand-transition>
            <div v-if="expandedGroups[item.entryGroupId]" class="mt-2 ml-4">
              <div 
                v-for="entry in item._groupEntries" 
                :key="entry.id"
                :class="['mb-1', entry.inventoryDecisionStatus === 'NEEDS_REVIEW' ? 'shortage-entry' : '']"
              >
                <div v-if="entry.inventoryDecisionStatus === 'NEEDS_REVIEW'" class="d-flex align-center warning--text">
                  <div class="flex-grow-1">
                    <div class="font-weight-bold">
                      {{ $vuetify.lang.t('$vuetify.worksheet.shortageEntry') }}:
                      {{ entry.netWeight | formatNumber }} {{ productInfo ? productInfo.measureWeight : "" }}
                    </div>
                    <div class="text-caption" v-if="entry.inventoryDecisionNote">
                      {{ formatShortageMessage(entry) }}
                    </div>
                    <div v-if="canResolveEntry(entry)" class="mt-2">
                      <v-btn
                        x-small
                        outlined
                        color="primary"
                        @click.stop="openResolutionDialog(entry)"
                      >
                        <v-icon x-small left>mdi-swap-horizontal</v-icon>
                        {{ $vuetify.lang.t("$vuetify.worksheet.inventoryResolution.reassign") }}
                      </v-btn>
                    </div>
                  </div>
                </div>
                <div v-else class="d-flex justify-space-between">
                  <div v-if="inventoryMap[entry.inventoryId]">
                    <a class="link" @click="viewInventory(entry.inventoryId)">
                      <span v-if="inventoryMap[entry.inventoryId].documentNumber"
                        >Doc #: {{ inventoryMap[entry.inventoryId].documentNumber }} /
                      </span>
                      ID: {{ inventoryMap[entry.inventoryId].id }}
                    </a>
                  </div>
                  <span class="grey--text text--darken-1">
                    {{ entry.netWeight | formatNumber }} {{ productInfo ? productInfo.measureWeight : "" }}
                  </span>
                </div>
              </div>
            </div>
          </v-expand-transition>
        </div>
        <div class="text-caption" v-else-if="item.inventoryId && inventoryMap[item.inventoryId]">
          <div v-if="inventoryMap[item.inventoryId].productName">
            {{ inventoryMap[item.inventoryId].productName }}
          </div>
          <a
            class="link"
            @click="viewSelectedInventory(item)"
          >
            <span v-if="inventoryMap[item.inventoryId].documentNumber"
              >Doc #: {{ inventoryMap[item.inventoryId].documentNumber }} /
            </span>
            ID: {{ inventoryMap[item.inventoryId].id }}
          </a>
        </div>
        <div v-if="!item._isGrouped && canResolveEntry(item)" class="mt-2">
          <v-btn
            x-small
            outlined
            color="primary"
            @click.stop="openResolutionDialog(item)"
          >
            <v-icon x-small left>mdi-swap-horizontal</v-icon>
            {{ $vuetify.lang.t("$vuetify.worksheet.inventoryResolution.reassign") }}
          </v-btn>
        </div>
      </template>
      <template v-slot:item.adjustmentWeight="{ item }">
        {{ item.netAdjustmentWeight > 0 ? "+" : ""
        }}{{ item.netAdjustmentWeight | formatNumber }}
        {{ productInfo ? productInfo.measureWeight : "" }}
      </template>
      <template v-slot:item.netWeight="{ item }">
        {{
          (item.netWeight +
            (item.netAdjustmentWeight ? item.netAdjustmentWeight : 0))
            | formatNumber
        }}
        {{ productInfo ? productInfo.measureWeight : "" }}
      </template>
      <template v-slot:item.files="{ item }">
        <div v-if="item.files && item.files.length > 0">
          <v-btn v-if="isMobile" text @click="showImages(item)"
            >{{ item.files.length }} File</v-btn
          >
          <div class="d-flex flex-row" v-else>
            <v-img
              :class="`${item.files.length > 1 ? 'mr-2' : ''} my-2`"
              v-for="(img, index) in getEntryImages(item)"
              :key="index"
              contain
              aspect-ratio="1.7"
              max-height="100"
              :src="img.src"
              @click="showImages(item)"
            />
          </div>
        </div>
      </template>
      <template v-slot:item.action="{ item }">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon>
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list class="pa-2" width="200px">
            <v-list-item
              @click="
                selectedEntry = item;
                adjustmentDialog = true;
              "
            >
              <v-list-item-icon><v-icon>mdi-plus</v-icon></v-list-item-icon>
              <v-list-item-content>
                {{ $vuetify.lang.t("$vuetify.base.adjustmentWeight") }}
              </v-list-item-content>
            </v-list-item>
            <!-- Straight to the Bluetooth printer beside this station. The
                 other Print Label item opens the print page, which is the right
                 thing when choosing sizes or printing a range — but a worker
                 holding a bundle with a torn sticker wants one tap, not three
                 screens. Shown only where a printer is actually set up. -->
            <v-list-item v-if="btReprintAvailable" @click="$emit('reprintEntry', item)">
              <v-list-item-icon
                ><v-icon>mdi-printer-wireless</v-icon></v-list-item-icon
              >
              <v-list-item-content>
                พิมพ์ป้ายใหม่ (บลูทูธ) / Reprint (Bluetooth)
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="canPrint" @click="printLabel(item)">
              <v-list-item-icon
                ><v-icon>mdi-printer-outline</v-icon></v-list-item-icon
              >
              <v-list-item-content>
                {{ $vuetify.lang.t("$vuetify.printSetting.printLabel") }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="worksheet.status == 'PENDING' && editable && !(item._isGrouped && item.entryGroupId)"
              @click="editEntry(item)"
            >
              <v-list-item-icon
                ><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon
              >
              <v-list-item-content>
                {{ $vuetify.lang.t("$vuetify.base.edit") }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="worksheet.status == 'PENDING' && editable"
              @click="deleteItem(item)"
            >
              <v-list-item-icon
                ><v-icon>mdi-delete-outline</v-icon></v-list-item-icon
              >
              <v-list-item-content>
                {{ $vuetify.lang.t("$vuetify.base.delete") }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="worksheet.status == 'PENDING' && canResolveEntry(item)"
              @click="openResolutionDialog(item)"
            >
              <v-list-item-icon
                ><v-icon>mdi-swap-horizontal</v-icon></v-list-item-icon
              >
              <v-list-item-content>
                {{ $vuetify.lang.t("$vuetify.worksheet.inventoryResolution.reassign") }}
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:body.append="{}">
        <tr>
          <td :colspan="headers.length">
            <div class="ma-4 text-subtitle-2">
              <div class="d-flex justify-content-between">
                <div>{{ $vuetify.lang.t("$vuetify.base.total") }}</div>
                <div class="ml-5">
                  {{ sum ? sum.netWeight : 0 | formatNumber }} {{ weightUnit }}
                </div>
              </div>
              <div
                v-if="worksheet.targetWeight && worksheet.targetWeight > 0"
                class="d-flex justify-content-between"
              >
                <div>{{ $vuetify.lang.t("$vuetify.base.target") }}</div>
                <div class="ml-5">
                  {{ worksheet.targetWeight | formatNumber }} {{ weightUnit }}
                </div>
              </div>
              <div
                v-if="worksheet.targetWeight && worksheet.targetWeight > 0"
                class="d-flex justify-content-between"
              >
                <div>{{ $vuetify.lang.t("$vuetify.base.remain") }}</div>
                <div class="ml-5">
                  {{ remainingWeight | formatNumber }} {{ weightUnit }}
                </div>
              </div>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-dialog style="z-index: 1000" v-model="imgDialog" fullscreen>
      <v-card>
        <v-card-title class="justify-end">
          <div>
            <v-btn @click="imgDialog = null" icon
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </div>
        </v-card-title>
        <v-card-text>
          <v-carousel continuous hide-delimiter-background v-model="imgIndex" height="80vh" v-if="images && images.length > 0">
            <v-carousel-item
              v-for="(img, i) in images"
              :key="i"
              reverse-transition="fade-transition"
              transition="fade-transition"
            >
            <v-container class="fill-height align-items-center justify-content-center">
              <v-layout class="flex-column" justify-center>
                <div class="d-flex justify-space-between">
                  <div class="text-subtitle-2 mb-2">
                    {{ $vuetify.lang.t("$vuetify.base.entry") }} {{img.entryIndex}}/{{images.length}}
                  </div>
                  <div>
                    <div v-if="img.grossWeight">
                      {{ $vuetify.lang.t("$vuetify.base.grossWeight") }}:
                      {{ img.grossWeight | formatNumber }}
                      {{ productInfo ? productInfo.measureWeight : "" }}
                    </div>
                    <div v-if="getDeductibleWeight(img) > 0">
                      {{ $vuetify.lang.t("$vuetify.base.deductibleWeight") }}:
                      {{ getDeductibleWeight(img) | formatNumber }}
                      {{ productInfo ? productInfo.measureWeight : "" }}
                    </div>
                    <div v-if="img.netWeight && img.netWeight != img.grossWeight"> 
                      {{ $vuetify.lang.t("$vuetify.base.netWeight") }}:
                      {{
                        (img.netWeight +
                          (img.netAdjustmentWeight ? img.netAdjustmentWeight : 0))
                          | formatNumber
                      }}
                      {{ productInfo ? productInfo.measureWeight : "" }}
                    </div>
                  </div>
                  <div class="text-caption ma-2">
                    {{ img.time | formatDateTime }}
                  </div>
                </div>
                <v-img :src="img.src" contain
                  aspect-ratio="1.7" ref="images"/>
              </v-layout>
            </v-container>
            </v-carousel-item>
          </v-carousel>
          <div v-else class="text-subtitle-2 font-weight-light">
            No attachments found
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      max-width="300px;"
      style="z-index: 1000"
      v-model="adjustmentDialog"
    >
      <v-card>
        <v-card-title class="justify-end">
          <div>
            <v-btn @click="adjustmentDialog = null" icon
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </div>
        </v-card-title>
        <v-card-text>
          <worksheet-item-adjustment
            :key="selectedEntry ? selectedEntry.id : 0"
            @cancel="adjustmentDialog = null"
            :worksheetEntry="selectedEntry"
            :productInfo="productInfo"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      max-width="720px"
      style="z-index: 1000"
      v-model="resolutionDialog"
    >
      <v-card>
        <v-card-title class="justify-space-between">
          <span>{{ $vuetify.lang.t("$vuetify.worksheet.inventoryResolution.title") }}</span>
          <v-btn icon @click="closeResolutionDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="resolutionEntry" class="text-subtitle-2 mb-3">
            {{ $vuetify.lang.t("$vuetify.worksheet.inventoryResolution.entryQuantity") }}
            {{ resolutionEntry.netWeight | formatNumber }}
            {{ productInfo ? productInfo.measureWeight : "" }}
          </div>
          <div v-if="resolutionEntry && resolutionEntry.inventoryDecisionNote" class="text-body-2 mb-4">
            {{ formatShortageMessage(resolutionEntry) }}
          </div>

          <div v-if="resolutionLoading" class="py-8 text-center">
            <v-progress-circular indeterminate color="primary" />
            <div class="mt-3">
              {{ $vuetify.lang.t("$vuetify.worksheet.inventoryResolution.loading") }}
            </div>
          </div>

          <v-alert
            v-else-if="resolutionError"
            type="error"
            outlined
            class="mb-4"
          >
            {{ resolutionError }}
          </v-alert>

          <v-alert
            v-else-if="resolutionOptions.length === 0"
            type="info"
            outlined
            class="mb-0"
          >
            {{
              $vuetify.lang.t(
                resolutionMode === "location"
                  ? "$vuetify.worksheet.inventoryResolution.noLocations"
                  : "$vuetify.worksheet.inventoryResolution.noInventories"
              )
            }}
          </v-alert>

          <v-list v-else two-line>
            <v-list-item
              v-for="option in resolutionOptions"
              :key="resolutionOptionKey(option)"
              class="px-0"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ resolutionOptionTitle(option) }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ resolutionOptionSubtitle(option) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  color="primary"
                  :loading="resolutionSubmitting"
                  :disabled="resolutionSubmitting"
                  @click="
                    resolutionMode === 'location'
                      ? resolveWithLocation(option)
                      : resolveWithInventory(option)
                  "
                >
                  {{ $vuetify.lang.t("$vuetify.worksheet.inventoryResolution.assign") }}
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="closeResolutionDialog">
            {{ $vuetify.lang.t("$vuetify.base.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <confirmation-dialog ref="confirm" />
    <v-bottom-sheet scrollable v-model="showInventoryDetails">
      <v-card>
        <v-card-title class="justify-center">
          <v-btn class="mt-6" text @click="showInventoryDetails = false">
            {{ $vuetify.lang.t("$vuetify.close") }}
          </v-btn>
        </v-card-title>
        <v-card-text>
          <inventory-details
            :key="selectedInventory ? selectedInventory.id : 0"
            v-model="selectedInventory"
          />
          <div v-if="selectedInventoryUnit">
            <div class="text-center text-subtitle-1 my-2 font-weight-light">
              {{ this.$vuetify.lang.t("$vuetify.worksheet.selectedUnit") }}
            </div>
            <v-simple-table class="table">
              <tr>
                <td>{{ $vuetify.lang.t("$vuetify.inventory.code") }}</td>
                <td>{{ selectedInventoryUnit.code }}</td>
              </tr>
              <tr>
                <td>
                  {{ $vuetify.lang.t("$vuetify.inventory.numberOfUnits", productInfo ? productInfo.measureUnit : "Unit") }}
                </td>
                <td>
                  {{ selectedInventoryUnit.unitShare }}
                  {{ productInfo ? productInfo.measureUnit : "" }}
                </td>
              </tr>
              <tr>
                <td>
                  {{ $vuetify.lang.t("$vuetify.inventory.weightPerUnit", productInfo ? productInfo.measureUnit : "Unit") }}
                </td>
                <td>
                  {{ selectedInventoryUnit.amountPerUnit | formatNumber }}
                  {{ selectedInventoryUnit.unitMeasure }}
                </td>
              </tr>
            </v-simple-table>
          </div>
        </v-card-text>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
import btPrinter from "@/utils/btPrinter.js";
import { mapActions } from "vuex";
const WorksheetItemAdjustment = () =>
  import("@/components/processWorksheet/WorksheetItemAdjustment.vue");
const InventoryDetails = () =>
  import("@/components/inventory/InventoryDetails.vue");
import { sumWorksheetWeight } from "@/services/worksheetServices";
const Download = () => import("@/components/common/Download.vue");
import moment from 'moment';
import env from '@/env';

export default {
  props: {
    worksheet: {
      type: Object,
      required: true,
    },
    worksheetTypeFields: {
      type: Object,
      required: true,
    },
    sum: {
      type: Object,
      required: true,
    },
    productInfo: {
      type: Object,
      required: false,
    },
    editable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => {
    return {
      imgDialog: false,
      imgIndex: 0,
      adjustmentDialog: false,
      selectedEntry: null,
      inventories: [],
      showInventoryDetails: false,
      selectedInventory: null,
      selectedInventoryUnit: null,
      coa: [],
      labResults: [],
      expandCard: false,
      missingCoaInventory: [],
      expandedGroups: {}, // Track which grouped entries are expanded
      resolutionDialog: false,
      resolutionLoading: false,
      resolutionSubmitting: false,
      resolutionEntry: null,
      resolutionMode: "inventory",
      resolutionOptions: [],
      resolutionError: null,
    };
  },
  watch: {
    worksheet: {
      handler(newVal) {
        console.log(newVal);
        this.sumWeight();
        this.loadInventoryDetails();
      },
      deep: true,
    },
  },
  computed: {
    // Create a lookup map for O(1) inventory access
    inventoryMap() {
      const map = {};
      if (this.inventories && this.inventories.length > 0) {
        this.inventories.forEach(inv => {
          map[inv.id] = inv;
        });
      }
      return map;
    },
    isMobile() {
      return this.$vuetify.breakpoint.name === "xs";
    },
    weightUnit() {
      return this.productInfo ? this.productInfo.measureWeight : null;
    },
    remainingWeight() {
      const target = this.worksheet.targetWeight
        ? this.worksheet.targetWeight
        : 0;
      const total = this.sum ? this.sum.netWeight : 0;
      const remainingWeight = target - total;
      return remainingWeight;
    },
    // Only where this phone has actually been set up with a printer — on the
    // rest it would be an action that silently does nothing.
    btReprintAvailable() {
      return btPrinter.isSupported() && btPrinter.hasRememberedPrinter();
    },
    // Printing used to mean a customer-facing sticker, so it was limited to
    // Output worksheets and anything with a customer. Bundles are labelled at
    // receiving too, and on those worksheets the action was simply missing —
    // a torn sticker had no way to be reprinted at all.
    canPrint() {
      return !!this.worksheet;
    },
    images() {
      const entries = this.groupedEntries;
      let images = [];
      if (
        entries && entries.length > 0
      ) {
        entries.forEach( (entry, index) => {
          if(entry.files && entry.files.length > 0){
            const entryFiles = entry.files.map((file) => {
              const src = `${env.VUE_APP_BASE_URL}/file/worksheet/${file.id}`;
              return { src, id: file.id, time: file.createdTime, entryIndex: index+1, 
                entryId: entry.id, 
                grossWeight: entry.grossWeight,
                netWeight: entry.netWeight, 
                netAdjustmentWeight: entry.netAdjustmentWeight};
            })
            
            images = images.concat(entryFiles);
          }
        })
      } 
      return images;
    },
    hasUnits() {
      const hasUnit = false;

      if (this.worksheet && this.worksheet.entries) {
        const entriesWithUnits = this.worksheet.entries.filter(
          (e) => e.unit && e.unit > 0
        );
        if (entriesWithUnits && entriesWithUnits.length > 0) {
          return true;
        }
      }

      return hasUnit;
    },
    headers() {
      const headers = [
        {
          text: "",
          value: "index",
          align: "start",
          sortable: true,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.base.time"),
          // createTime is the weighing time and is stable; updateTime moves on
          // every write. See the slot above.
          value: "createTime",
          align: "start",
          sortable: true,
        },
      ];

      if (this.hasUnits) {
        headers.push({
          text: this.$vuetify.lang.t("$vuetify.base.unit"),
          value: "unit",
          align: "start",
          sortable: true,
        });
      }

      headers.push({
        text: this.$vuetify.lang.t("$vuetify.base.grossWeight"),
        value: "grossWeight",
        align: "start",
        sortable: true,
      });

      headers.push({
        text: this.$vuetify.lang.t("$vuetify.base.adjustmentWeight"),
        value: "adjustmentWeight",
        align: "start",
        sortable: true,
      });

      headers.push({
        text: this.$vuetify.lang.t("$vuetify.base.netWeight"),
        value: "netWeight",
        align: "start",
        sortable: true,
      });

      if (this.hasNotes) {
        headers.push({
          text: this.$vuetify.lang.t("$vuetify.base.notes"),
          value: "notes",
          align: "start",
          sortable: true,
        });
      }

      if (this.hasFiles) {
        headers.push({
          text: this.$vuetify.lang.t("$vuetify.base.attachments"),
          value: "files",
          align: "start",
          sortable: true,
        });
      }

      headers.push({
        text: "",
        value: "action",
        align: "right",
        sortable: false,
      });

      return headers;
    },
    hasDeductibleWeight() {
      return (
        this.worksheetTypeFields.hasDeductibleWeight &&
        this.worksheet.deductibleWeight > 0
      );
    },
    hasFiles() {
      return this.worksheetTypeFields.hasEntryPhoto;
    },
    hasNotes() {
      return this.worksheetTypeFields.hasEntryNotes;
    },
    hasInventoryShortages() {
      if (!this.worksheet || !this.worksheet.entries) return false;
      return this.worksheet.entries.some(entry => entry.inventoryDecisionStatus === 'NEEDS_REVIEW');
    },
    shortageCount() {
      if (!this.worksheet || !this.worksheet.entries) return 0;
      return this.worksheet.entries.filter(entry => entry.inventoryDecisionStatus === 'NEEDS_REVIEW').length;
    },
    // Group entries by entry_group_id for display
    // Multiple entries (from FIFO/LIFO) sharing the same entry_group_id will be displayed as one row
    groupedEntries() {
      if (!this.worksheet || !this.worksheet.entries || this.worksheet.entries.length === 0) {
        return [];
      }

      // Group entries by entryGroupId
      const groups = {};
      this.worksheet.entries.forEach(entry => {
        const groupId = entry.entryGroupId || entry.id;
        if (!groups[groupId]) {
          groups[groupId] = [];
        }
        groups[groupId].push(entry);
      });

      // Create aggregated entries for display
      return Object.values(groups).map(groupEntries => {
        // Use the first entry as the base
        const firstEntry = groupEntries[0];
        
        // If single entry, return as-is
        if (groupEntries.length === 1) {
          return firstEntry;
        }

        // For multiple entries, aggregate values
        const aggregated = { ...firstEntry };
        aggregated._isGrouped = true;
        aggregated._groupEntries = groupEntries;
        
        // Sum up weights
        aggregated.netWeight = groupEntries.reduce((sum, e) => sum + (e.netWeight || 0), 0);
        // Gross weight is the user's physical scale reading — same for all entries in the group
        aggregated.grossWeight = firstEntry.grossWeight;
        aggregated.netAdjustmentWeight = groupEntries.reduce((sum, e) => sum + (e.netAdjustmentWeight || 0), 0);
        
        // Collect all inventory IDs
        aggregated._inventoryIds = groupEntries
          .filter(e => e.inventoryId)
          .map(e => e.inventoryId);
        
        // Use the first entry's create time for display
        aggregated.createTime = firstEntry.createTime;
        aggregated.updateTime = firstEntry.updateTime;
        
        // Combine files from all entries
        aggregated.files = groupEntries.reduce((all, e) => {
          if (e.files && e.files.length > 0) {
            return all.concat(e.files);
          }
          return all;
        }, []);

        // Combine notes from all entries (if they exist and are different)
        const allNotes = groupEntries
          .map(e => e.notes)
          .filter(n => n && n.trim())
          .filter((note, index, self) => self.indexOf(note) === index); // unique notes only
        aggregated.notes = allNotes.join('\n---\n');

        return aggregated;
      });
    },
    invertedLabResults(){
      if(this.labResults){
        const arr = this.labResults;
        const inverted = arr.reduce((acc, cur) => {
            acc[cur.symbol] = cur.percentage;
            return acc;
          }, {Date: moment().format('DD/MM/YYYY'), Time: moment().format('hh:mm:ss'), SampleNo: '', Quality: '', AverOutN: '', Program: '', QMDelete: '', dateTime: moment().format('DD/MM/YYYY hh:mm:ss'),});
        return [inverted];
      }
      else{
        return this.labResults;
      }
    }
  },
  components: {
    InventoryDetails,
    WorksheetItemAdjustment,
    Download
  },
  methods: {
    ...mapActions("messages", ["addErrorMessage", "addSuccessMessage"]),
    async sumWeight() {
      this.sum = await sumWorksheetWeight(this.worksheet);
      this.$emit("update:sum", this.sum);
    },
    canResolveEntry(entry) {
      return Boolean(
        entry &&
          this.editable &&
          this.worksheet &&
          this.worksheet.status === "PENDING" &&
          ["NEEDS_REVIEW", "BACKORDER"].includes(entry.inventoryDecisionStatus)
      );
    },
    usesLocationResolution() {
      return ["FIFO", "LIFO"].includes(
        ((this.worksheet && this.worksheet.processInputMethod) || "").toUpperCase()
      );
    },
    async openResolutionDialog(entry) {
      if (!this.canResolveEntry(entry)) {
        return;
      }

      this.resolutionEntry = entry;
      this.resolutionDialog = true;
      this.resolutionLoading = true;
      this.resolutionSubmitting = false;
      this.resolutionOptions = [];
      this.resolutionError = null;
      this.resolutionMode = this.usesLocationResolution() ? "location" : "inventory";

      try {
        const params = {
          productId: this.worksheet.productId,
        };

        const url =
          this.resolutionMode === "location"
            ? "/processWorksheetInput/available-locations"
            : "/processWorksheetInput/available-inventories";
        const resp = await this.$axios.get(url, { params });
        const requestedQuantity = Number(entry.netWeight || 0);
        const options = Array.isArray(resp.data) ? resp.data : [];

        this.resolutionOptions = options.filter((option) => {
          const available = Number(
            this.resolutionMode === "location"
              ? option.totalWeightAvailable
              : option.weightAvailable
          );

          return requestedQuantity <= 0 || available >= requestedQuantity;
        });
      } catch (error) {
        const message =
          (error && error.data && error.data.message) ||
          this.$vuetify.lang.t("$vuetify.worksheet.inventoryResolution.loadError");
        this.resolutionError = message;
        this.addErrorMessage(message);
      } finally {
        this.resolutionLoading = false;
      }
    },
    closeResolutionDialog(force = false) {
      if (this.resolutionSubmitting && !force) {
        return;
      }
      this.resolutionDialog = false;
      this.resolutionLoading = false;
      this.resolutionSubmitting = false;
      this.resolutionEntry = null;
      this.resolutionOptions = [];
      this.resolutionError = null;
    },
    resolutionOptionKey(option) {
      if (this.resolutionMode === "location") {
        return option.locationId != null ? `loc-${option.locationId}` : "loc-none";
      }
      return `inv-${option.id}`;
    },
    resolutionOptionTitle(option) {
      if (this.resolutionMode === "location") {
        return (
          option.locationName ||
          this.$vuetify.lang.t("$vuetify.worksheet.inventoryResolution.noLocation")
        );
      }
      return option.documentNumber
        ? `${option.documentNumber} / #${option.id}`
        : `#${option.id}`;
    },
    resolutionOptionSubtitle(option) {
      const availableLabel = this.$vuetify.lang.t("$vuetify.worksheet.inventoryResolution.available");
      const availableValue = this.formatResolutionQuantity(
        this.resolutionMode === "location" ? option.totalWeightAvailable : option.weightAvailable
      );
      const weightUnit = this.productInfo ? this.productInfo.measureWeight : "";

      if (this.resolutionMode === "location") {
        const inventoryCount = option.inventoryCount || 0;
        return `${availableLabel}: ${availableValue} ${weightUnit} | ${inventoryCount} ${this.$vuetify.lang.t("$vuetify.worksheet.inventoryResolution.inventoryCount")}`;
      }

      const pieces = [`${availableLabel}: ${availableValue} ${weightUnit}`];
      if (option.locationName) {
        pieces.push(option.locationName);
      }
      if (option.inventoryDate) {
        pieces.push(moment(option.inventoryDate).format("YYYY-MM-DD HH:mm"));
      }
      return pieces.join(" | ");
    },
    formatResolutionQuantity(value) {
      const numericValue = Number(value || 0);
      return numericValue.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    async resolveWithLocation(option) {
      if (!this.resolutionEntry) {
        return;
      }

      this.resolutionSubmitting = true;
      try {
        const params = {};
        if (option.locationId != null) {
          params.locationId = option.locationId;
        }
        if (this.worksheet.processInputMethod) {
          params.selectionMethod = this.worksheet.processInputMethod;
        }

        await this.$axios.post(
          `/processWorksheetInput/${this.resolutionEntry.id}/resolve-with-location`,
          null,
          { params }
        );

        this.addSuccessMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.inventoryResolution.success")
        );
        this.closeResolutionDialog(true);
        this.$emit("resolvedEntry");
      } catch (error) {
        const message =
          (error && error.data && error.data.message) ||
          this.$vuetify.lang.t("$vuetify.worksheet.inventoryResolution.resolveError");
        this.addErrorMessage(message);
      } finally {
        this.resolutionSubmitting = false;
      }
    },
    async resolveWithInventory(option) {
      if (!this.resolutionEntry || !option || !option.id) {
        return;
      }

      this.resolutionSubmitting = true;
      try {
        await this.$axios.post(
          `/processWorksheetInput/${this.resolutionEntry.id}/resolve-with-inventory`,
          null,
          { params: { inventoryId: option.id } }
        );

        this.addSuccessMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.inventoryResolution.success")
        );
        this.closeResolutionDialog(true);
        this.$emit("resolvedEntry");
      } catch (error) {
        const message =
          (error && error.data && error.data.message) ||
          this.$vuetify.lang.t("$vuetify.worksheet.inventoryResolution.resolveError");
        this.addErrorMessage(message);
      } finally {
        this.resolutionSubmitting = false;
      }
    },
    loadInventoryDetails() {
      if (this.worksheet.entries) {
        const inventoryIds = this.worksheet.entries
          .filter((e) => e.inventoryId)
          .map((e) => e.inventoryId);
        if (inventoryIds && inventoryIds.length > 0) {
          const params = {
            ids: inventoryIds ? inventoryIds.join(",") : null,
            liteMode: false,
          };

          this.$axios
            .get("/inventory/by-ids", { params })
            .then(async (resp) => {
              this.inventories = resp.data;
              this.coa = this.getCOAWithFinalTestTrue(this.inventories);
              const coaResults = await this.fetchLabResultsForCOA(this.coa);
              this.labResults = this.calculateWeightedAveragePercentages(
                this.worksheet.entries,
                this.coa,
                coaResults
              );

              if (this.inventories && this.inventories.length > 0) {
                this.inventories.forEach((i) => {
                  if (i.fields && i.fields.length > 0) {
                    const docNumberField = i.fields.find((f) =>
                      f.field.toLowerCase().includes("document no")
                    );
                    i.documentNumber = docNumberField
                      ? docNumberField.fieldValueString
                      : "";
                  }
                });
              }
            });
        }
      }
    },
    getCOAWithFinalTestTrue(inventoryList) {
      // Create an array to hold the filtered coa objects
      let filteredCOA = [];

      // Loop through each inventory object in the inventoryList
      inventoryList.forEach((inventory) => {
        // Get the coa array from the inventory object
        const coaArray = inventory.coa;

        // Filter the coa array to only include objects with finalTest = true
        const filteredArray = coaArray ? coaArray.filter((coa) => coa && coa.finalTest === true):[];

        // Add the filtered coa objects to the filteredCOA array
        filteredCOA = filteredCOA.concat(filteredArray);
      });

      return filteredCOA;
    },
    async fetchLabResultsForCOA(coa) {
      const labTestIds = coa.map((c) => c.id);
      const params = {
        labReportIds: labTestIds.join(","),
      };
      return await this.$axios
        .get("/inventory-lab-result/get-composition-average", { params })
        .then((resp) => {
          return resp.data;
        });
    },
    getInventory(id) {
      if (this.inventories && this.inventories.length > 0) {
        return this.inventories.find((i) => i.id == id);
      } else {
        return {};
      }
    },
    async deleteItem(entry) {
      if (this.worksheet.entries) {
        const isGrouped = entry._isGrouped && entry.entryGroupId;
        const confirmMessage = isGrouped 
          ? `Do you want to delete this grouped entry (${entry._groupEntries.length} inventories)?`
          : "Do you want to delete this entry?";
        
        let confirm = await this.$refs.confirm.open(
          "Confirm Delete",
          confirmMessage
        );
        if (confirm) {
          // If grouped, delete the entire group; otherwise delete single entry
          const deleteUrl = isGrouped
            ? `processWorksheetInput/group/${entry.entryGroupId}`
            : `processWorksheetInput/${entry.id}`;
          
          this.$axios
            .delete(deleteUrl)
            .then(() => {
              this.$emit("deletedEntry", entry);
            })
            .catch((error) => {
              this.addErrorMessage(
                "Error deleting entry, " + error.data.message
              );
            });
        }
      }
    },
    editEntry(entry) {
      // Block editing of FIFO/LIFO grouped entries
      if (entry._isGrouped && entry.entryGroupId) {
        this.addErrorMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.error.cannotEditGroupedEntry")
        );
        return;
      }
      this.$emit("editEntry", entry);
    },
    showImages(entry) {
      this.imgDialog = true;
      this.selectedEntry = entry;
      const entryImgIndex = this.images.findIndex(i => i.entryId == entry.id);
      this.imgIndex = entryImgIndex > -1 ? entryImgIndex:0;
    },
    getEntryImages(entry) {
      if (entry && entry.files.length > 0) {
        return entry.files.map((file) => {
          const src = `${env.VUE_APP_BASE_URL}/file/worksheet/${file.id}`;
          return { src, id: file.id };
        });
      } else {
        return [];
      }
    },
    print() {
      if (this.worksheet.customerId) {
        this.$router.push({
          name: "worksheetPackingSlip",
          params: { id: this.worksheet.id },
        });
      } else {
        this.$router.push({
          name: "processWorksheetPrintQR",
          params: { id: this.worksheet.id },
        });
      }
    },
    printLabel(entry) {
      // From the row menu, deep-link the label page to just this entry's
      // sticker index (?entries=N); the toolbar button prints all labels.
      let query;
      if (entry) {
        const index =
          this.groupedEntries.findIndex((e) => e.id == entry.id) + 1;
        if (index > 0) query = { entries: String(index) };
      }
      this.$router.push({
        name: "processWorksheetPrintLabel",
        params: { id: this.worksheet.id },
        query,
      });
    },
    viewSelectedInventory(worksheetEntry) {
      const inventoryId = worksheetEntry.inventoryId;
      const inventoryUnitId = worksheetEntry.inventoryUnitId;
      this.selectedInventory = this.getInventory(inventoryId);
      if (inventoryUnitId && this.selectedInventory.unitDetails) {
        this.selectedInventoryUnit = this.selectedInventory.unitDetails.find(
          (d) => d.id == inventoryUnitId
        );
      }
      this.showInventoryDetails = true;
    },
    viewInventory(inventoryId) {
      if (inventoryId) {
        this.$router.push(`/factory/inventory/view/${inventoryId}`);
      }
    },
    toggleGroupExpansion(entryGroupId) {
      if (entryGroupId) {
        this.$set(this.expandedGroups, entryGroupId, !this.expandedGroups[entryGroupId]);
      }
    },
    getTotalReservedWeight(groupEntries) {
      if (!groupEntries || !Array.isArray(groupEntries)) return 0;
      return groupEntries.reduce((sum, entry) => sum + (entry.netWeight || 0), 0);
    },
    getDeductibleWeight() {
      // Deductible weight is a worksheet-level constant, not per-entry
      return this.worksheet.deductibleWeight || 0;
    },
    parseShortageNote(note) {
      // Parse structured note like: [SHORTFALL|locationId:123|locationName:Location Name|available:14035.00|requested:20000.00|shortfall:5965.00]
      if (!note) {
        return null;
      }

      const normalizedNote = note.trim();
      
      // Handle new structured format
      const structuredMatch = normalizedNote.match(/^\[SHORTFALL\|([^\]]+)\]([\s\S]*)$/);
      if (structuredMatch) {
        const data = {};
        const parts = structuredMatch[1].split('|');
        
        parts.forEach(part => {
          const colonIndex = part.indexOf(':');
          if (colonIndex > 0) {
            const key = part.substring(0, colonIndex).trim();
            const value = part.substring(colonIndex + 1).trim();
            
            if (key === 'available' || key === 'requested' || key === 'shortfall') {
              data[key] = parseFloat(value);
            } else {
              data[key] = value;
            }
          }
        });

        const trailingNote = structuredMatch[2]
          ? structuredMatch[2].replace(/[\[\]]/g, ' ').replace(/\s+/g, ' ').trim()
          : '';
        if (trailingNote) {
          data.trailingNote = trailingNote;
        }
        
        return data;
      }
      
      // Handle old format: [Auto-created: Insufficient inventory at location ID 255. Available: 14035.000000, Requested: 20000, Shortfall: 5965.000000...]
      if (note.startsWith('[Auto-created:')) {
        const data = {};
        
        // Extract location ID
        const locationMatch = note.match(/location ID (\d+)/);
        if (locationMatch) {
          data.locationId = locationMatch[1];
        }
        
        // Extract available
        const availableMatch = note.match(/Available:\s*([\d.]+)/);
        if (availableMatch) {
          data.available = parseFloat(availableMatch[1]);
        }
        
        // Extract requested
        const requestedMatch = note.match(/Requested:\s*([\d.]+)/);
        if (requestedMatch) {
          data.requested = parseFloat(requestedMatch[1]);
        }
        
        // Extract shortfall
        const shortfallMatch = note.match(/Shortfall:\s*([\d.]+)/);
        if (shortfallMatch) {
          data.shortfall = parseFloat(shortfallMatch[1]);
        }
        
        return data;
      }
      
      return null;
    },
    formatShortageMessage(entry) {
      const parsed = this.parseShortageNote(entry.inventoryDecisionNote);
      if (!parsed) {
        return entry.inventoryDecisionNote || this.$vuetify.lang.t('$vuetify.worksheet.pendingReviewDescription');
      }
      
      // Format numbers with thousands separator
      const formatNum = (num) => {
        if (num == null) return '0';
        return num.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 2});
      };
      
      // Build localized location part
      let locationPart = '';
      if (parsed.locationName) {
        const locTemplate = this.$vuetify.lang.t('$vuetify.worksheet.shortage.atLocation');
        locationPart = locTemplate.replace('{0}', parsed.locationName);
      } else if (parsed.locationId) {
        const locIdTemplate = this.$vuetify.lang.t('$vuetify.worksheet.shortage.atLocationId');
        locationPart = locIdTemplate.replace('{0}', parsed.locationId);
      }
      
      const weightUnit = this.productInfo ? this.productInfo.measureWeight : '';
      
      // Get the message template and replace placeholders
      // Use Vuetify's lang.t() with parameters directly
      return this.$vuetify.lang.t(
        '$vuetify.worksheet.shortage.message',
        locationPart,
        formatNum(parsed.available),
        formatNum(parsed.requested),
        formatNum(parsed.shortfall),
        weightUnit
      );
    },
    calculateWeightedAveragePercentages(entries, coaList, labresults) {
      this.missingCoaInventory = []
      const result = {};

      let totalNetWeight = 0;
      const listOfElementDistributions = [];
      const symbolToNameMap = {};

      // loop through each entry to calculate the distribution of its net weight among each element
      entries.forEach((entry) => {
        
        const inventoryId = entry.inventoryId;
        const coa = coaList.find((c) => c.inventoryId == inventoryId);

        //skip calculation if entry can't be matched with a coa
        if(!coa){
          this.missingCoaInventory.push(inventoryId);
          return;
        }

        totalNetWeight += entry.netWeight;
        const coaLabResults = labresults.filter((l) => l.labTestId == coa.id);

        const elementDistribution = {};
        coaLabResults.forEach((elementResult) => {
          symbolToNameMap[elementResult.symbol] = elementResult.elementName;

          elementDistribution[elementResult.symbol] =
            entry.netWeight * (elementResult.averagePercentage / 100);
        });
        listOfElementDistributions.push(elementDistribution);
      });

      // sum the element distributions for each element to get the total amount of each element
      const totals = {};
      listOfElementDistributions.forEach((elementDistribution) => {
        Object.keys(elementDistribution).forEach((elementSymbol) => {
          if (!totals[elementSymbol]) {
            totals[elementSymbol] = 0;
          }
          totals[elementSymbol] += elementDistribution[elementSymbol];
        });
      });

      // calculate the weighted average percentage for each element and add it to the result object
      Object.keys(totals).forEach((elementSymbol) => {
        result[elementSymbol] = (totals[elementSymbol] / totalNetWeight) * 100;
      });

      // convert result object to an array
      return Object.keys(result).map((key) => {
        const name = symbolToNameMap[key];
        return { name, symbol: key, percentage: result[key] };
      });
    },
  },
  created() {
    // console.log(this.worksheet.entries);
    // debugger;
    this.sumWeight();
    this.loadInventoryDetails();
  },
};
</script>

<style scoped>
a.link {
  color: green;
}

.fullscreen {
  height: 100vh; /* set height to viewport height */
}

.shortage-entry {
  background-color: #fff8e1;
  border-left: 3px solid #ff9800;
  padding: 8px;
  margin: 4px 0;
  border-radius: 4px;
}
</style>
